import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore.js";
import { UsernameField } from "../components/auth/UsernameField.js";
import { useI18n } from "../i18n/index.js";

type Tab = "login" | "register";

// ============================================================
// Shared input component
// ============================================================

function Field({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  disabled,
  autoComplete,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoComplete?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm text-white/70">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white
                   placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-white/30
                   transition-colors disabled:opacity-50"
      />
    </div>
  );
}

// ============================================================
// Register form
// ============================================================

function RegisterForm() {
  const { register, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    clearError();
    await register(username, email, password);
    if (!useAuthStore.getState().error) {
      navigate("/start");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <UsernameField value={username} onChange={setUsername} disabled={isLoading} />

      <Field
        label={t("auth.email")}
        type="email"
        value={email}
        onChange={setEmail}
        placeholder={t("auth.emailPlaceholder")}
        disabled={isLoading}
        autoComplete="email"
      />

      <Field
        label={t("auth.password")}
        type="password"
        value={password}
        onChange={setPassword}
        placeholder={t("auth.passwordPlaceholder")}
        disabled={isLoading}
        autoComplete="new-password"
      />

      {error && (
        <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isLoading || !username || !email || !password}
        className="w-full py-2.5 rounded-lg bg-white/20 hover:bg-white/30 font-medium text-sm
                   transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isLoading ? t("auth.loading") : t("auth.register")}
      </button>

      <SocialDivider />
    </form>
  );
}

// ============================================================
// Login form
// ============================================================

function LoginForm() {
  const { login, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    clearError();
    await login(email, password);
    if (!useAuthStore.getState().error) {
      navigate("/start");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Field
        label={t("auth.email")}
        type="email"
        value={email}
        onChange={setEmail}
        placeholder={t("auth.emailPlaceholder")}
        disabled={isLoading}
        autoComplete="email"
      />

      <Field
        label={t("auth.password")}
        type="password"
        value={password}
        onChange={setPassword}
        placeholder={t("auth.passwordLoginPlaceholder")}
        disabled={isLoading}
        autoComplete="current-password"
      />

      {error && (
        <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isLoading || !email || !password}
        className="w-full py-2.5 rounded-lg bg-white/20 hover:bg-white/30 font-medium text-sm
                   transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isLoading ? t("auth.loading") : t("auth.loginBtn")}
      </button>

      <SocialDivider />
    </form>
  );
}

// ============================================================
// "Oder" divider + Social buttons (coming soon)
// ============================================================

function SocialDivider() {
  const { t } = useI18n();
  return (
    <>
      <div className="flex items-center gap-3 my-1">
        <hr className="flex-1 border-white/15" />
        <span className="text-white/30 text-xs">{t("auth.or")}</span>
        <hr className="flex-1 border-white/15" />
      </div>

      <button
        type="button"
        disabled
        title={t("auth.googleSoon")}
        className="w-full py-2.5 rounded-lg border border-white/15 bg-white/5 text-white/30
                   text-sm flex items-center justify-center gap-2 cursor-not-allowed"
      >
        <GoogleIcon />
        {t("auth.googleLogin")}
        <span className="text-xs text-white/20 ml-1">{t("auth.googleComingSoon")}</span>
      </button>
    </>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" opacity=".5" />
      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".5" />
      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".5" />
      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" opacity=".5" />
    </svg>
  );
}

// ============================================================
// LoginPage — Haupt-Komponente
// ============================================================

interface Props {
  defaultTab?: Tab;
}

export function LoginPage({ defaultTab = "login" }: Props) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>(defaultTab);
  const { clearError } = useAuthStore();
  const { t } = useI18n();

  function switchTab(t: Tab) {
    setTab(t);
    clearError();
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--table-bg)] px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <p className="text-4xl mb-2">🃏</p>
          <h1 className="text-2xl font-bold tracking-wider text-white">SOLITAIRE</h1>
        </div>

        {/* Card */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-white/15">
            {(["login", "register"] as Tab[]).map((tabVal) => (
              <button
                key={tabVal}
                type="button"
                onClick={() => switchTab(tabVal)}
                className={[
                  "flex-1 py-3 text-sm font-medium transition-colors",
                  tab === tabVal
                    ? "text-white border-b-2 border-white -mb-px bg-white/5"
                    : "text-white/50 hover:text-white/75",
                ].join(" ")}
              >
                {tabVal === "login" ? t("auth.loginTab") : t("auth.registerTab")}
              </button>
            ))}
          </div>

          {/* Form */}
          <div className="p-6">
            {tab === "login" ? <LoginForm /> : <RegisterForm />}
          </div>
        </div>

        {/* Back to game (als Gast) */}
        <div className="text-center mt-5">
          <button
            type="button"
            onClick={() => navigate("/start")}
            className="text-white/40 hover:text-white/60 text-sm transition-colors"
          >
            {t("auth.playWithout")}
          </button>
        </div>
      </div>
    </div>
  );
}
